/**
 * WebSocketProvider - Provider globale per connessione WebSocket real-time
 *
 * Gestisce la connessione WebSocket per l'intera applicazione:
 * - Connessione automatica quando l'utente è autenticato
 * - Disconnessione automatica al logout
 * - Riconnessione automatica con backoff esponenziale
 * - Integrazione con React Query per cache invalidation
 */

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useWebSocket, type WebSocketStatus, type WebSocketMessage } from "@/hooks/use-websocket";
import { storage } from "@/lib/local-storage";

interface WebSocketContextType {
  /** Stato corrente della connessione */
  status: WebSocketStatus;
  /** Ultimo messaggio ricevuto */
  lastMessage: WebSocketMessage | null;
  /** Connetti manualmente al WebSocket */
  connect: () => void;
  /** Disconnetti dal WebSocket */
  disconnect: () => void;
  /** True se la connessione è attiva */
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

/**
 * Hook per accedere al contesto WebSocket
 * @throws Error se usato fuori da WebSocketProvider
 */
export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error("useWebSocketContext must be used within a WebSocketProvider");
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
  /** Disabilita connessione automatica (default: false) */
  disableAutoConnect?: boolean;
  /** Mostra toast per eventi documento (default: true) */
  showToasts?: boolean;
}

export const WebSocketProvider = ({
  children,
  disableAutoConnect = false,
  showToasts = true,
}: WebSocketProviderProps) => {
  // Verifica se l'utente è autenticato
  const isAuthenticated = !!storage.getItem("access_token");

  // Connetti solo se autenticato e autoConnect non è disabilitato
  const shouldConnect = isAuthenticated && !disableAutoConnect;

  const {
    status,
    lastMessage,
    connect,
    disconnect,
    isConnected,
  } = useWebSocket({
    autoConnect: shouldConnect,
    showToasts,
    autoReconnect: true,
    maxReconnectAttempts: 10,
    reconnectDelay: 1000,
  });

  // Disconnetti quando l'utente fa logout (token rimosso)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      const prefix = import.meta.env.VITE_PREFIX || '';
      const tokenKey = prefix ? `${prefix}_access_token` : 'access_token';

      // Se il token viene rimosso, disconnetti
      if (e.key === tokenKey && !e.newValue) {
        disconnect();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [disconnect]);

  // Log stato connessione in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log(`[WebSocketProvider] Status: ${status}, Authenticated: ${isAuthenticated}`);
    }
  }, [status, isAuthenticated]);

  return (
    <WebSocketContext.Provider
      value={{
        status,
        lastMessage,
        connect,
        disconnect,
        isConnected,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
